import { ArrowRight, Check, CheckCircle2, CircleDashed, Lock, Search, ShoppingCart, UserCheck, Wallet, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { changeSponsor, getOnboardingState, searchSponsorCandidates, verifySponsor } from '@/api/member';

/**
 * Getting Started — top-of-dashboard 3-step onboarding panel.
 *
 *   Step 1 — Verify Your Sponsor
 *   Step 2 — Deposit Funds
 *   Step 3 — Purchase Positions
 *
 * Only the active step is interactive; locked steps are dimmed and
 * unclickable. The section auto-hides once all three steps are done
 * AND for the god account (no human sponsor to verify).
 */

const POLL_INTERVAL_MS = 120000;
const DEBOUNCE_MS = 220;

const Step1Sponsor = ({ state, onChanged, isActive }) => {
  const [mode, setMode] = useState('idle'); // idle | search | confirm
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // Debounced autocomplete
  useEffect(() => {
    if (mode !== 'search') return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchSponsorCandidates(query.trim());
        setResults(data?.results || []);
      } catch (_e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, mode]);

  const handleVerify = async () => {
    setBusy(true);
    try {
      await verifySponsor();
      toast.success('Sponsor verified');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Could not verify sponsor.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await changeSponsor(selected);
      toast.success(`Sponsor set to @${selected}`);
      setMode('idle');
      setQuery('');
      setSelected('');
      setResults([]);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Could not change sponsor.');
    } finally {
      setBusy(false);
    }
  };

  const disabled = !isActive || busy;

  // -------- IDLE mode (default) --------
  if (mode === 'idle') {
    return (
      <div className="flex flex-col gap-3" data-testid="getting-started-step-1-idle">
        <div
          className="rounded-lg px-3 py-2.5 border"
          style={{
            background: 'rgba(0,0,0,0.32)',
            borderColor: 'rgba(255,182,18,0.18)',
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">Your sponsor on file</div>
          <div className="font-display font-bold text-lg mt-0.5 sc-gold-text truncate" data-testid="getting-started-sponsor-username">
            {state.sponsor_username ? `@${state.sponsor_username}` : '— none —'}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleVerify}
            disabled={disabled || !state.sponsor_username}
            className="sc-btn-gold"
            style={{ padding: '10px 16px', fontSize: 12, flex: 1, minWidth: 120 }}
            data-testid="getting-started-verify-btn"
          >
            <Check className="w-3.5 h-3.5" />
            Verify
          </button>
          <button
            type="button"
            onClick={() => setMode('search')}
            disabled={disabled}
            className="sc-btn-ghost"
            style={{ padding: '10px 16px', fontSize: 12, flex: 1, minWidth: 140 }}
            data-testid="getting-started-not-my-sponsor-btn"
          >
            Not My Sponsor
          </button>
        </div>
      </div>
    );
  }

  // -------- SEARCH mode --------
  return (
    <div className="flex flex-col gap-3" data-testid="getting-started-step-1-search">
      <div
        className="rounded-lg border flex items-center gap-2 px-3 py-2"
        style={{
          background: 'rgba(0,0,0,0.32)',
          borderColor: 'rgba(255,182,18,0.28)',
        }}
      >
        <Search className="w-4 h-4 text-[#FFC20E] shrink-0" />
        <input
          // biome-ignore lint/a11y/noAutofocus: focus the sponsor search field the member just opened via "search"
          autoFocus
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected('');
          }}
          placeholder="Type your sponsor's username…"
          disabled={disabled}
          className="flex-1 bg-transparent border-0 outline-none text-sm text-white placeholder:text-[var(--sc-text-mute)]"
          data-testid="getting-started-sponsor-search-input"
        />
        <button
          type="button"
          onClick={() => {
            setMode('idle');
            setQuery('');
            setSelected('');
            setResults([]);
          }}
          className="text-[var(--sc-text-mute)] hover:text-white"
          aria-label="Cancel search"
          data-testid="getting-started-cancel-search-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Autocomplete results */}
      {query.trim() && (
        <div
          className="rounded-lg border max-h-44 overflow-y-auto"
          style={{
            background: 'rgba(0,0,0,0.42)',
            borderColor: 'rgba(255,182,18,0.14)',
          }}
          data-testid="getting-started-sponsor-results"
        >
          {searching && <div className="px-3 py-2 text-[11px] text-[var(--sc-text-mute)]">Searching…</div>}
          {!searching && results.length === 0 && <div className="px-3 py-2 text-[11px] text-[var(--sc-text-mute)]">No matches for "{query.trim()}".</div>}
          {!searching &&
            results.map((r) => (
              <button
                key={r.username}
                type="button"
                onClick={() => setSelected(r.username)}
                className="w-full text-left px-3 py-2 text-sm transition-colors"
                style={{
                  background: selected === r.username ? 'rgba(255,182,18,0.14)' : 'transparent',
                  color: selected === r.username ? '#FFC20E' : '#e7e7e7',
                  borderTop: '1px solid rgba(255,182,18,0.05)',
                }}
                data-testid={`getting-started-sponsor-result-${r.username}`}
              >
                @{r.username}
              </button>
            ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled || !selected}
        className="sc-btn-gold"
        style={{ padding: '10px 16px', fontSize: 12 }}
        data-testid="getting-started-submit-sponsor-btn"
      >
        {selected ? `Submit @${selected}` : 'Select a sponsor first'}
        {selected && <ArrowRight className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

const StepCard = ({
  index,
  icon: Icon,
  title,
  description,
  status, // "active" | "locked" | "done"
  onClick,
  cta,
  children,
  testId,
}) => {
  const isActive = status === 'active';
  const isLocked = status === 'locked';
  const isDone = status === 'done';

  // Visual styling per state
  const borderColor = isActive ? 'rgba(255,194,14,0.62)' : isDone ? 'rgba(82, 200, 120, 0.42)' : 'rgba(255,255,255,0.06)';
  const glow = isActive ? '0 0 0 1px rgba(255,194,14,0.32), 0 18px 44px -16px rgba(255,182,18,0.42)' : 'none';
  const opacity = isLocked ? 0.42 : 1;
  const grayscale = isLocked ? 'grayscale(0.6)' : 'none';

  return (
    <section
      className="sc-glass rounded-2xl p-5 relative overflow-hidden flex flex-col gap-4"
      style={{
        borderColor,
        boxShadow: glow,
        opacity,
        filter: grayscale,
        transition: 'opacity 200ms ease, box-shadow 220ms ease, transform 200ms ease',
      }}
      data-testid={testId}
      data-status={status}
    >
      {/* Step index pill + lock/check overlay */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border"
            style={{
              background: isActive ? 'rgba(255,194,14,0.16)' : isDone ? 'rgba(82,200,120,0.16)' : 'rgba(255,255,255,0.04)',
              borderColor: isActive ? 'rgba(255,194,14,0.48)' : isDone ? 'rgba(82,200,120,0.42)' : 'rgba(255,255,255,0.08)',
              color: isActive ? '#FFC20E' : isDone ? '#7ee297' : '#9c9c9c',
            }}
          >
            {isDone ? <CheckCircle2 className="w-4 h-4" /> : index}
          </div>
          <Icon
            className="w-4 h-4"
            style={{
              color: isActive ? '#FFC20E' : isDone ? '#7ee297' : '#9c9c9c',
            }}
          />
        </div>
        {isLocked && <Lock className="w-4 h-4 text-[var(--sc-text-mute)]" />}
        {isDone && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: '#7ee297' }}>
            Done
          </span>
        )}
      </div>

      <div>
        <h3 className="font-display font-semibold text-base text-white leading-tight">{title}</h3>
        <p className="text-[12px] text-[var(--sc-text-mute)] mt-1 leading-snug">{description}</p>
      </div>

      {/* Body: either custom children (Step 1) or a CTA button (Steps 2 & 3) */}
      <div className="mt-auto">
        {children || (
          <button
            type="button"
            onClick={onClick}
            disabled={!isActive}
            className="sc-btn-gold w-full"
            style={{ padding: '10px 16px', fontSize: 12 }}
            data-testid={`${testId}-cta`}
          >
            {cta}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </section>
  );
};

const GettingStartedSection = ({ onNavigate }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const data = await getOnboardingState();
      if (mountedRef.current) setState(data);
    } catch (_e) {
      // Silent — section just hides on error.
      if (mountedRef.current) setState(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [refresh]);

  // Render guards
  if (loading) {
    return (
      <section className="sc-glass rounded-2xl p-5 mb-6 animate-pulse" aria-hidden="true" data-testid="getting-started-skeleton">
        <div className="h-4 w-40 bg-white/5 rounded mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-44 bg-white/5 rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  // Hide for god account or once everything is complete.
  if (!state || state.is_hidden || state.is_complete) {
    return null;
  }

  const step = state.current_step || 1;
  const status = (n) => {
    if (n < step) return 'done';
    if (n === step) return 'active';
    return 'locked';
  };

  return (
    <section className="mb-6" aria-label="Getting Started onboarding steps" data-testid="getting-started-section" data-current-step={step}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center border shrink-0"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
          >
            <CircleDashed className="w-4 h-4 text-[#FFC20E]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Getting Started</div>
            <div className="font-display font-semibold text-sm text-white">
              {step === 1 && 'Step 1 of 3 — Verify your sponsor'}
              {step === 2 && 'Step 2 of 3 — Deposit funds'}
              {step === 3 && 'Step 3 of 3 — Purchase positions'}
            </div>
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--sc-text-mute)' }}>
          <span style={{ color: step > 1 ? '#7ee297' : '#FFC20E' }}>●</span>
          <span style={{ color: step > 2 ? '#7ee297' : step === 2 ? '#FFC20E' : '#3a3a3f' }}>●</span>
          <span style={{ color: step === 3 ? '#FFC20E' : '#3a3a3f' }}>●</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StepCard
          index={1}
          icon={UserCheck}
          title="Verify Your Sponsor"
          description={
            state.sponsor_username
              ? 'Confirm that the person who referred you is correct — or pick the right one. Required before you can deposit, transfer in, or be PIFed by another member.'
              : 'Choose who referred you to 1SimpleCycler. Required before you can deposit, transfer in, or be PIFed by another member.'
          }
          status={status(1)}
          testId="getting-started-step-1"
        >
          {status(1) === 'active' ? (
            <Step1Sponsor state={state} onChanged={refresh} isActive />
          ) : status(1) === 'done' ? (
            <div
              className="rounded-lg px-3 py-2.5 border"
              style={{
                background: 'rgba(82,200,120,0.08)',
                borderColor: 'rgba(82,200,120,0.22)',
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">Verified sponsor</div>
              <div className="font-display font-bold text-base mt-0.5 truncate" style={{ color: '#7ee297' }}>
                @{state.sponsor_username || '—'}
              </div>
            </div>
          ) : null}
        </StepCard>

        <StepCard
          index={2}
          icon={Wallet}
          title="Deposit Funds"
          description="Send LTC, TRX, or DOGE to your permanent deposit address. Auto-credits to your Purchase Balance."
          status={status(2)}
          onClick={() => onNavigate?.('purchase')}
          cta="Go to Deposit Funds"
          testId="getting-started-step-2"
        />

        <StepCard
          index={3}
          icon={ShoppingCart}
          title="Purchase Positions"
          description="Buy your first position to enter the 3×1 matrix and start cycling."
          status={status(3)}
          onClick={() => onNavigate?.('purchase')}
          cta="Go to Purchase Positions"
          testId="getting-started-step-3"
        />
      </div>
    </section>
  );
};

export default GettingStartedSection;
